/**
 * Query Router — SLIMMED (Phase 3)
 *
 * Previously: 2,517-line monolithic handler with 15+ intent type switch cases.
 * Now: Thin shell that provides state management, AdminPMTiles creation, and a
 * safety-net route() method. All actual intent handling is done by pipeline ops.
 *
 * Kept because main.js still uses:
 *   - Constructor: creates AdminPMTilesLayer, stores references
 *   - update(): syncs locationIndex, countriesData, adminBoundaries
 *   - getLastSQL(): feeds the "Show SQL" UI panel
 *   - interpolateResponse(): formats voice response templates
 *   - adminPMTiles: PMTiles layer controller reference
 *   - speechOutput: voice synthesis reference
 *   - _lastSQL, _lastResultCount, _lastResultRow: result state for UI
 *   - route(): safety-net fallback (should never fire in normal operation)
 *
 * Handlers extracted to pipeline ops/ in Phase 1-2:
 *   navigate, terrain, show, choropleth, filter, compare, unknown,
 *   overture_layer, rivers, choropleth_style, choropleth_scale,
 *   duckdb_query, forced_poi, boundaries
 * Composition op added in Phase 3:
 *   overlay_cities
 *
 * Intent types handled BEFORE route() is reached:
 *   - educational → main.js (streaming, lines 518-533)
 *   - reset → main.js (direct state clearing, lines 498-509)
 *   - bivariate_choropleth → Step 2 SQL path (has intent.sql)
 *   - All others → Step 3 legacy adapter → pipeline executor
 */

import { AdminPMTilesLayer } from '../layers/admin-pmtiles-layer.js';
import { adminBoundaries as _defaultAdminBoundaries } from '../data/admin-boundaries.js';
import { interpolateTemplate } from './pipeline-utils.js';
import { adviseSpatialMethod } from './spatial-advisor.js';
import { classificationEngine } from '../layers/classification-engine.js';

class QueryRouter {
  constructor({ animator, layerManager, speechOutput, locationIndex, countriesData }) {
    this.animator = animator;
    this.layers = layerManager;
    this.speech = speechOutput;
    this.speechOutput = speechOutput; // alias used by main.js
    this.locationIndex = locationIndex;
    this.countriesData = countriesData;
    this.adminBoundaries = _defaultAdminBoundaries;
    this.adminPMTiles = new AdminPMTilesLayer(animator.globe);
    this._lastSQL = null;
    this._lastResultCount = null;
    this._lastResultRow = null;
    this._spokeDuringRoute = false;
    this._lastSpatialAdvice = null;
    this.onResultUpdate = null; // callback for UI result panel
  }

  /**
   * Return the Spatial Analysis Advisor enrichment produced by the most
   * recent route() call. Consumed by the overlay result panel to render
   * the Spatial Insights card.
   */
  getLastSpatialAdvice() {
    return this._lastSpatialAdvice;
  }

  /**
   * Update references (e.g., when new data loads)
   */
  update({ locationIndex, countriesData, adminBoundaries }) {
    if (locationIndex) this.locationIndex = locationIndex;
    if (countriesData) this.countriesData = countriesData;
    if (adminBoundaries) this.adminBoundaries = adminBoundaries;
  }

  /**
   * Get the last SQL query executed (for "Show SQL" panel)
   */
  getLastSQL() {
    return this._lastSQL;
  }

  /**
   * Interpolate a voice response template with actual result data.
   * Call after route() to replace {count} etc. with real values.
   * Delegates to the pipeline-utils version.
   */
  interpolateResponse(template) {
    if (!template) return template;
    return interpolateTemplate(template, {
      resultCount: this._lastResultCount,
      resultRow: this._lastResultRow,
    });
  }

  /**
   * Route a parsed intent through the pipeline.
   *
   * In the main processCommand() flow, intents are routed via Steps 1-3
   * before reaching this method. However, the lesson system and other callers
   * may invoke route() directly, so it delegates to the pipeline adapter.
   *
   * If the adapter can't handle the intent (educational, unknown), speaks
   * a generic fallback.
   */
  async route(intent) {
    this._lastSQL = null;
    this._lastResultCount = null;
    this._lastResultRow = null;
    this._spokeDuringRoute = false;

    // Spatial Analysis Advisor enrichment — runs before dispatch so that
    // classification-engine and choropleth-style-manager can consume the
    // hint when they render. Wrapped in try/catch so advisor failures
    // never block the pipeline.
    let spatialAdvice = null;
    try {
      spatialAdvice = adviseSpatialMethod(intent);
      this._lastSpatialAdvice = spatialAdvice;
      classificationEngine.setAdvisorHint({
        suggestedClassification: spatialAdvice.ev_classification,
        color_ramp_type: spatialAdvice.color_ramp_type,
        method: spatialAdvice.method,
      });
      const styleMgr = this.layers && this.layers.choroplethStyleManager;
      if (styleMgr && typeof styleMgr.setAdvisorHint === 'function') {
        styleMgr.setAdvisorHint(spatialAdvice);
      }
    } catch (err) {
      console.debug('[spatial-advisor] advisor failed, continuing:', err);
      spatialAdvice = null;
    }

    try {
      // Delegate to pipeline via adapter
      const { adaptIntent } = await import('./legacy-adapter.js');
      const { executePipeline, createContext } = await import('./pipeline-executor.js');

      const adapted = adaptIntent(intent);
      if (adapted.handled) {
        const ctx = createContext({
          animator: this.animator,
          layers: this.layers,
          speech: this.speech,
          locationIndex: this.locationIndex,
          countriesData: this.countriesData,
          adminBoundaries: this.adminBoundaries,
          adminPMTiles: this.adminPMTiles,
          duckdb: null,
          onResultUpdate: this.onResultUpdate,
          onTimeSliderShow: this.onTimeSliderShow,
          spatialAdvice,
          suggestedOp: spatialAdvice && spatialAdvice.confidence > 0.8 ? spatialAdvice.ev_op : null,
          suggestedClassification: spatialAdvice ? spatialAdvice.ev_classification : null,
          suggestedLayerType: spatialAdvice ? spatialAdvice.ev_layer_type : null,
          suggestedColorRampType: spatialAdvice ? spatialAdvice.color_ramp_type : null,
        });
        await executePipeline(adapted.pipeline, ctx);

        // Sync results back to router state for UI compatibility
        this._lastSQL = ctx.lastSQL;
        this._lastResultCount = ctx.resultCount;
        this._lastResultRow = ctx.resultRow;
        this._spokeDuringRoute = ctx.spokeDuringRoute;
        return;
      }
    } catch (err) {
      console.error('[QueryRouter] Pipeline delegation failed:', err);
    } finally {
      // Clear the advisor hint so it doesn't leak into the next op.
      classificationEngine.setAdvisorHint(null);
    }

    // Fallback: unhandled intent type
    console.warn('[QueryRouter] Unhandled intent type:', intent?.type);
    if (this.speech) {
      await this.speech.speak("I'm not sure how to handle that. Try asking differently.");
      this._spokeDuringRoute = true;
    }
  }
}

export { QueryRouter };
export default QueryRouter;
